import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import { VirtualConnectionTableQuery } from '../../../../../gen-src/queries';
import { FeatureFlagsContext, messageWithOrWithoutCount, SelectionProviderChildProps, SiteUrlsContext, } from '../../../data-catalog-shared';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { ColumnDetailsDialogQueryWrapper } from '../ColumnDetails/ColumnDetailsDialog';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { LineageItemType } from '../lineage/LineageItemType';
import {
  getAnchorCountFromConfig,
  LineageItemConfig,
  lineageItemKey,
  LineageSection,
  LineageSectionType,
} from '../placePage/LineageConfiguration';
import { VirtualConnectionTableLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import {
  VirtualConnectionTableLineageCounts,
  VirtualConnectionTableLineageCountsQueryWrapper,
} from './VirtualConnectionTableCountsQueryWrapper';
import {
  memoizedGetVirtualConnectionTableLineageConfiguration,
  VirtualConnectionTableLineagePaneParams,
} from './VirtualConnectionTableLineageConfiguration';
import { VirtualConnectionTableQueryWrapper } from './VirtualConnectionTableQueryWrapper';
import { VirtualConnectionTableSubplaceParamsProvider } from './VirtualConnectionTableSubplaceParamsProvider';

const noop = () => { return; };

export interface VirtualConnectionTableLineageSubplaceRouterProps {
  readonly vizportalUrlId: string;
}

export function VirtualConnectionTableLineageSubplaceRouter(props: VirtualConnectionTableLineageSubplaceRouterProps): JSX.Element {
  const { lensLineageEnabled } = React.useContext(FeatureFlagsContext);
  const urls: SiteUrls = React.useContext(SiteUrlsContext);
  const renderVirtualConnectionTableLineageRoute = React.useCallback((): JSX.Element => {
    return (
      <Switch>
        {memoizedGetVirtualConnectionTableLineageConfiguration(lensLineageEnabled).map(lineageSection =>
          lineageSection.items.map((lineageItem, i) =>
            <Route
              key={i}
              path={lineageItem.path(urls, props.vizportalUrlId)}
              render={() => renderLineageItemRoute(urls, lineageItem, lineageSection, props.vizportalUrlId)}
            />
          )
        )}
        <Redirect
          exact={true}
          from={urls.virtualConnectionTableLineagePath(props.vizportalUrlId)}
          to={urls.virtualConnectionTableLineageFieldsPath(props.vizportalUrlId)}
        />
        <UnrecognizedUrl
          backToButtonLabel={Messages.BackToFields()}
          backToButtonUrl={urls.virtualConnectionTableLineagePath(props.vizportalUrlId)}
        />
      </Switch>
    );
  }, [props.vizportalUrlId, urls, lensLineageEnabled]);

  return (
    <SiteUrlsContext.Consumer>
      {urls =>
        <Route
          path={urls.virtualConnectionTableLineagePath(props.vizportalUrlId)}
          render={() => renderVirtualConnectionTableLineageRoute()}
        />
      }
    </SiteUrlsContext.Consumer>
  );
}

function renderLineageItemRoute(
  urls: SiteUrls,
  lineageItem: LineageItemConfig<VirtualConnectionTableLineageCounts, VirtualConnectionTableLineagePaneParams>,
  lineageSection: LineageSection<VirtualConnectionTableLineageCounts, VirtualConnectionTableLineagePaneParams>,
  vizportalUrlId: string,
): JSX.Element {
  return (
    <VirtualConnectionTableSubplaceParamsProvider>
      {params =>
        <ErrorListProvider>
          <ErrorToasts />
          <VirtualConnectionTableQueryWrapper vizportalUrlId={vizportalUrlId}>
            {virtualConnection =>
              <VirtualConnectionTableLineageCountsQueryWrapper
                vizportalUrlId={vizportalUrlId}
                selectedColumnKeys={params.selectedColumns}
              >
                {({ countsLoading, unfilteredCounts, filteredCounts }) =>
                  <>
                    <VirtualConnectionTableLineageSubplace
                      vizportalUrlId={vizportalUrlId}
                      virtualConnection={virtualConnection}
                      lineageItem={lineageItem}
                      lineageSection={lineageSection}
                      countsLoading={countsLoading}
                      unfilteredCounts={unfilteredCounts}
                      filteredCounts={filteredCounts}
                      urlSearchValue={params.urlSearchValue}
                      selectedKeys={params.selectedColumns}
                      updateSelectionForKey={params.updateColumnSelection}
                      clearColumnSelection={params.clearColumnSelection}
                    />
                    {lineageSection.sectionType === LineageSectionType.Anchor ?
                      <Route
                        path={urls.virtualConnectionTableLineageFieldPath(vizportalUrlId, ':columnId')}
                        render={({ history, location, match }: RouteComponentProps<{ columnId: string }>) => {
                          return <ColumnDetailsDialogQueryWrapper
                            columnId={match.params.columnId}
                            tableVizportalId={vizportalUrlId}
                            onClose={() => history.replace({
                              ...location,
                              pathname: urls.virtualConnectionTableLineageFieldsPath(vizportalUrlId)
                            })}
                            afterDescriptionAndTagsChange={noop}
                          />;
                        }
                        }
                      />
                      : null
                    }
                  </>
                }
              </VirtualConnectionTableLineageCountsQueryWrapper>
            }
          </VirtualConnectionTableQueryWrapper>
        </ErrorListProvider>
      }
    </VirtualConnectionTableSubplaceParamsProvider>
  );
}

type VirtualConnectionTableLineageSubplaceProps = {
  readonly vizportalUrlId: string;
  readonly virtualConnection: Unpacked<VirtualConnectionTableQuery['virtualConnectionTables']>;
  readonly lineageItem: LineageItemConfig<VirtualConnectionTableLineageCounts, VirtualConnectionTableLineagePaneParams>;
  readonly lineageSection: LineageSection<VirtualConnectionTableLineageCounts, VirtualConnectionTableLineagePaneParams>;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: VirtualConnectionTableLineageCounts | null;
  readonly filteredCounts: VirtualConnectionTableLineageCounts | null;
  readonly urlSearchValue: string;
  readonly clearColumnSelection: () => void;
} & SelectionProviderChildProps;

function VirtualConnectionTableLineageSubplace(props: VirtualConnectionTableLineageSubplaceProps) {
  const { lensLineageEnabled } = React.useContext(FeatureFlagsContext);
  const selectedColumns = Object.keys(props.selectedKeys);
  const hasFilter = selectedColumns.length > 0;
  const filteredColumnId: string | null = hasFilter ? selectedColumns[0] : null;
  const lineageCounts = props.filteredCounts ? props.filteredCounts : props.unfilteredCounts;
  return (
    <>
      <BrowserTitleUpdater
        name={props.virtualConnection.name}
        browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_ENTITY_NAME_AND_SUBSPACE_NAME}
        subspaceName={Messages.LineageNavHeader()}
      />
      <VirtualConnectionTableLineageSubplaceLayout
        activeLineageItemKey={lineageItemKey(props)}
        selectedAnchorItemIds={selectedColumns}
        pane={props.lineageItem.paneFactory({
          title: messageWithOrWithoutCount(
            lineageCounts && props.lineageItem.count(lineageCounts),
            props.lineageItem.messagesForPane
          ),
          selectedKeys: props.selectedKeys,
          updateSelectionForKey: props.updateSelectionForKey,
          vizportalUrlId: props.vizportalUrlId,
          filteredColumnId,
          count: lineageCounts && props.lineageItem.count(lineageCounts),
          virtualConnectionTableLuid: props.virtualConnection.luid,
        })}
        tableId={props.vizportalUrlId}
        countsLoading={props.countsLoading}
        unfilteredCounts={props.unfilteredCounts}
        filteredCounts={props.filteredCounts}
        urlSearchValue={props.urlSearchValue}
        clearColumnSelection={props.clearColumnSelection}
        anchorName={props.virtualConnection.name}
        anchorCount={getAnchorCountFromConfig(
          memoizedGetVirtualConnectionTableLineageConfiguration(lensLineageEnabled),
          props.unfilteredCounts
        )}
        filteredOnItemType={hasFilter ? LineageItemType.VirtualConnection : null}
        filteredItemName={props.filteredCounts?.filteredFieldName ?? null}
        lineageSectionType={props.lineageSection.sectionType}
      />
    </>
  );
}
